// Types
import { TChangeRequestCallback } from "@/Washouts/hooks/ChangeRequests/useChangeRequestSubscriptions";
import { IGetChangeRequest, IGetChangeRequestsByCommissionCycle } from "@/Washouts/types/PayrollClosing/queries";
import { IPositionChangeRequest } from "@/Washouts/types/WashoutReview";
import { QueryClient } from "@tanstack/react-query";
import { cloneDeep } from "lodash";

export const createOrUpdateChangeRequestCallback: TChangeRequestCallback = (response, queryClient, isCreate = false) => {
	if (!response) {
		return;
	}

	updateChangeRequests(queryClient, response);
	updatePositionsWithChangeRequests(queryClient, response);
	updateSupportRequestTree(queryClient, response, isCreate);
};

const isMatchesAllKeys = (origin: object, response: object, keys: string[] = []) =>
	keys.every((key) => {
		const originValue = origin[key as keyof object];
		const responseValue = response[key as keyof object];
		if (!originValue || !responseValue) {
			return false;
		}
		return originValue === responseValue;
	});

const updateApproveAllChangeRequestsStatuses = (queryClient: QueryClient, response: IGetChangeRequestsByCommissionCycle) => {
	const caches = queryClient.getQueriesData<IGetChangeRequestsByCommissionCycle>({
		queryKey: ["positionsWithChangeRequestsStatus"],
		exact: false,
	});

	caches.forEach(([queryKey]) => {
		const [_identifier, locationId, _, payPeriodId] = queryKey;
		const isMatchAllKeys = isMatchesAllKeys({ locationId, payPeriodId }, response, ["locationId", "payPeriodId"]);

		if (isMatchAllKeys) {
			queryClient.invalidateQueries({ queryKey, exact: true });
		}
	});
};

const updateChangeRequests = (queryClient: QueryClient, response: IGetChangeRequestsByCommissionCycle) => {
	const changeRequestData = queryClient.getQueriesData<IGetChangeRequestsByCommissionCycle>({
		queryKey: ["changeRequests"],
		exact: false,
	});

	if (!changeRequestData?.length) {
		return;
	}

	changeRequestData.forEach(([queryKey, data]) => {
		const [_, commissionCycleId, payPeriodId, position, userId, origin, escalated] = queryKey;

		if (
			!data?.requests ||
			!checkIfResponseMatchesKey({
				commissionCycleId,
				payPeriodId,
				position,
				userId,
				response,
			})
		) {
			return;
		}
		const filterFn = (changeRequest: IGetChangeRequest) => checkIfChangeRequestsMatchesKey({ escalated, origin, changeRequest });

		queryClient.setQueryData<IGetChangeRequestsByCommissionCycle>(queryKey, (data) => {
			if (!data) {
				return data;
			}

			const clonedData = cloneDeep(data);
			const requestUpdated = updateRequestArrayOrAdd(clonedData.requests, response.requests[0]);
			const requests = requestUpdated.filter(filterFn);

			return {
				...clonedData,
				requests,
			};
		});
	});

	updateApproveAllChangeRequestsStatuses(queryClient, response);
};

const updatePositionsWithChangeRequests = (queryClient: QueryClient, response: IGetChangeRequestsByCommissionCycle) => {
	const positionsWithChangeRequestsData = queryClient.getQueriesData<IPositionChangeRequest>({
		queryKey: ["positionsWithChangeRequestsStatus"],
		exact: false,
	});

	const isPending = response.requests[0]?.status === "PENDING";

	if (!positionsWithChangeRequestsData?.length) {
		return;
	}

	positionsWithChangeRequestsData.forEach(([queryKey]) => {
		const [_, locationId, commissionCycleId, payPeriodId] = queryKey;

		if (
			locationId !== response.locationId ||
			commissionCycleId !== response.commissionCycleId ||
			payPeriodId !== response.payPeriodId
		) {
			return;
		}

		queryClient.setQueryData<IPositionChangeRequest[]>(queryKey, (data) => {
			if (!data) {
				return data;
			}

			return data.map((item) => {
				if (item.position !== response.position) {
					return item;
				}

				const { pendingRequestIds } = item;

				if (!isPending) {
					return {
						...item,
						pendingRequestIds: pendingRequestIds.filter((id) => id !== response.requests[0]._id),
					};
				}

				const newPendingRequestIds = Array.from(new Set([...pendingRequestIds, response.requests[0]._id]));
				return {
					...item,
					pendingRequestIds: newPendingRequestIds,
				};
			});
		});
	});
};

const updateSupportRequestTree = (queryClient: QueryClient, response: IGetChangeRequestsByCommissionCycle, isCreate: boolean) => {
	if (isCreate || response.requests[0].status !== "PENDING") {
		queryClient.invalidateQueries({ queryKey: ["supportRequestsTree", response.locationId] });
	}
};

const updateRequestArrayOrAdd = (data: IGetChangeRequest[], updated: IGetChangeRequest) => {
	let found = false;
	const newArray = data.map((item) => {
		if (item._id === updated._id) {
			found = true;
			return updated;
		}
		return item;
	});
	if (!found) {
		newArray.push(updated);
	}
	return newArray.filter((item) => item.status !== "DELETED");
};

interface ICheckIfResponseMatchesArgs {
	commissionCycleId: unknown;
	payPeriodId: unknown;
	position: unknown;
	userId: unknown;
	response: IGetChangeRequestsByCommissionCycle;
}

const checkIfResponseMatchesKey = ({ commissionCycleId, payPeriodId, position, userId, response }: ICheckIfResponseMatchesArgs) => {
	if (commissionCycleId !== "" && response.commissionCycleId !== commissionCycleId) {
		return false;
	}
	if (payPeriodId !== "" && response.payPeriodId !== payPeriodId) {
		return false;
	}
	if (position !== "" && response.position !== position) {
		return false;
	}
	if (userId !== "" && response.userId !== userId) {
		return false;
	}

	return true;
};

interface ICheckIfChangeRequestsMatchesKeyArgs {
	escalated: unknown;
	origin: unknown;
	changeRequest: IGetChangeRequest;
}

const checkIfChangeRequestsMatchesKey = ({ escalated, origin, changeRequest }: ICheckIfChangeRequestsMatchesKeyArgs) => {
	if (origin !== "" && changeRequest.origin !== origin) {
		return false;
	}
	if (escalated !== "" && changeRequest.supportAssigned !== escalated) {
		return false;
	}

	return true;
};
